import { memo, useCallback, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import type { ComposerCapabilityCategory, ComposerCapabilityExample } from '@/types/composer';
import { CapabilityCategoryPillTray } from '@/components/chat/capability-category-pill-tray';
import { getCapabilityMenuInChatScrollClassName } from '@/components/chat/chat-message-styles';
import { ComposerCapabilityDrawer } from '@/components/chat/composer-capability-drawer';
import { useCapabilityCategoryDrawer } from '@/hooks/use-capability-category-drawer';
import { useHorizontalScrollEdgeFades } from '@/hooks/use-horizontal-scroll-edge-fades';
import { cn } from '@/lib/utils';

export type ComposerCapabilityMenuVariant =
  /** Welcome capability menu — pills below the composer in welcome empty-chat. */
  | 'welcome'
  /** In-chat capability strip — horizontal pills above the composer in the thread dock. */
  | 'inChat';

interface ComposerCapabilityMenuProps {
  categories: ComposerCapabilityCategory[];
  variant?: ComposerCapabilityMenuVariant;
  onExampleHover?: (example: ComposerCapabilityExample | null) => void;
  onExampleSelect: (example: ComposerCapabilityExample) => void;
  /** Notified when a category drawer opens/closes (e.g. to keep the latest message visible in-chat). */
  onOpenChange?: (open: boolean) => void;
  className?: string;
  disabled?: boolean;
  initialOpenCategoryId?: string | null;
}

/** Memoized: re-renders only when categories/variant/handlers change, not on parent keystrokes. */
export const ComposerCapabilityMenu = memo(function ComposerCapabilityMenu({
  categories,
  variant = 'welcome',
  onExampleHover,
  onExampleSelect,
  onOpenChange,
  className,
  disabled = false,
  initialOpenCategoryId = null,
}: ComposerCapabilityMenuProps) {
  const [inChatDrawerPosition, setInChatDrawerPosition] = useState<{
    left: number;
    width: number;
    bottom: number;
  } | null>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const drawerHostRef = useRef<HTMLDivElement>(null);
  const scrollRef = useRef<HTMLDivElement>(null);

  const getDismissIgnoreNodes = useCallback(
    () => [menuRef.current, drawerHostRef.current],
    []
  );

  const {
    openCategoryId,
    openCategory,
    toggleCategory,
    handleExampleSelect,
  } = useCapabilityCategoryDrawer({
    categories,
    onExampleHover,
    onExampleSelect,
    onOpenChange,
    initialOpenCategoryId,
    getDismissIgnoreNodes,
  });

  const isInChat = variant === 'inChat';

  const { maskStyle, onWheel } = useHorizontalScrollEdgeFades(scrollRef, {
    active: isInChat,
    measureKey: categories,
  });

  useLayoutEffect(() => {
    if (!isInChat || !openCategory) {
      setInChatDrawerPosition(null);
      return;
    }

    const updatePosition = () => {
      const anchor = menuRef.current;
      if (!anchor) return;
      const rect = anchor.getBoundingClientRect();
      setInChatDrawerPosition({
        left: rect.left,
        width: rect.width,
        bottom: window.innerHeight - rect.top + 4,
      });
    };

    updatePosition();
    window.addEventListener('resize', updatePosition);
    const anchor = menuRef.current;
    const observer = anchor ? new ResizeObserver(updatePosition) : null;
    if (anchor && observer) {
      observer.observe(anchor);
    }

    return () => {
      window.removeEventListener('resize', updatePosition);
      observer?.disconnect();
    };
  }, [isInChat, openCategory, openCategoryId]);

  if (!categories.length) return null;

  const drawerContent =
    openCategory && (openCategory.examples.length > 0 || openCategory.streaming) ? (
      <ComposerCapabilityDrawer
        category={openCategory}
        onExampleHover={onExampleHover}
        onExampleSelect={handleExampleSelect}
        className="pointer-events-auto max-w-full"
      />
    ) : null;

  const inlineDrawer =
    drawerContent && !isInChat ? (
      <div className="pointer-events-auto absolute left-0 right-0 top-full z-30 mt-1">
        {drawerContent}
      </div>
    ) : null;

  const portaledDrawer =
    drawerContent && isInChat && inChatDrawerPosition
      ? createPortal(
          <div
            ref={drawerHostRef}
            className="pointer-events-auto fixed z-50"
            style={{
              left: inChatDrawerPosition.left,
              width: inChatDrawerPosition.width,
              bottom: inChatDrawerPosition.bottom,
            }}
          >
            {drawerContent}
          </div>,
          document.body
        )
      : null;

  const tray = (
    <CapabilityCategoryPillTray
      categories={categories}
      openCategoryId={openCategoryId}
      variant={variant}
      disabled={disabled}
      onToggleCategory={toggleCategory}
    />
  );

  return (
    <div ref={menuRef} className={cn('relative w-full min-w-0', className)}>
      {isInChat ? (
        <div className="relative w-full min-w-0">
          <div
            ref={scrollRef}
            className={getCapabilityMenuInChatScrollClassName()}
            style={maskStyle}
            onWheel={onWheel}
          >
            {tray}
          </div>
        </div>
      ) : (
        tray
      )}
      {inlineDrawer}
      {portaledDrawer}
    </div>
  );
});
